import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from 'react-query';

import { useServiceError } from '@/hooks';

import { PageRequest } from '@/core/request/PageRequest';

import {
  BankAccountsFilterQuery,
  BankAccountsQuery,
  CreateBankAccountRequest,
  UpdateBankAccountRequest,
} from './BankAccounts.types';

import {
  listBankAccounts,
  deleteBankAccount,
  createBankAccount,
  updateBankAccount,
  getBankAccount,
  updateStatusBankAccount,
  listBankAccountsFilter,
} from './BankAccounts.service';

import { DEFAULT_ORDER, DEFAULT_ROWS } from '..';
import { message, notification } from 'antd';
import { useIntl } from 'react-intl';
import notificationError from '@/utils/functions/notificationError';
import { queryClient } from '@/Root';
import { useQueryChanel } from '@/contexts/QueryChanelProvider';

export const BANK_ACCOUNT_FILTER_KEY = 'bank/accounts/filter';

export function useBankAccounts(params?: BankAccountsQuery) {
  const query = useInfiniteQuery(
    ['bankAccounts', { ...params }],
    ({ pageParam }) => {
      const req: PageRequest = {
        nextToken: pageParam,
        order: params?.order || DEFAULT_ORDER,
        rows: params?.rows || DEFAULT_ROWS,
        ...params,
      };

      return listBankAccounts(req);
    },
    {
      getNextPageParam: (lastPage) => lastPage.nextToken,
    },
  );

  useServiceError(query.error);

  return query;
}

export function useBankAccountsFilter(params?: BankAccountsFilterQuery) {
  const cache = queryClient.getQueryData([
    BANK_ACCOUNT_FILTER_KEY,
    { ...params },
  ]);

  return useInfiniteQuery(
    [BANK_ACCOUNT_FILTER_KEY, { ...params }],
    ({ pageParam }) => {
      const req: PageRequest = {
        nextToken: pageParam,
        order: params?.order || DEFAULT_ORDER,
        rows: params?.rows || DEFAULT_ROWS,
        ...params,
      };

      return listBankAccountsFilter(req);
    },
    {
      getNextPageParam: (lastPage) => lastPage.nextToken,
      enabled: !cache,
      onError(error) {
        notificationError(error);
      },
    },
  );
}

export function useBankAccount(id?: number) {
  const query = useQuery(['bankAccount', id], () => getBankAccount(id), {
    enabled: !!id,
  });

  useServiceError(query.error);

  return query;
}

export function useBankAccountMutation() {
  const mutation = useMutation((id: number) => getBankAccount(id));

  useServiceError(mutation.error);

  return mutation;
}

export function useCreateBankAccount() {
  const intl = useIntl();
  const queryChanel = useQueryChanel();

  const mutation = useMutation(
    (obj: CreateBankAccountRequest) => createBankAccount(obj),
    {
      onSuccess() {
        notification.success({
          message: intl.formatMessage({
            id: 'bankAccounts.create.notification.success',
          }),
          description: intl.formatMessage({
            id: 'bankAccounts.create.notification.successDescription',
          }),
        });

        queryChanel({
          resetQueries: [[BANK_ACCOUNT_FILTER_KEY]],
        });
      },
    },
  );

  useServiceError(mutation.error);

  return mutation;
}

export function useUpdateBankAccount() {
  const intl = useIntl();
  const queryChanel = useQueryChanel();

  const mutation = useMutation(
    (obj: UpdateBankAccountRequest) => updateBankAccount(obj),
    {
      onSuccess(_, data) {
        notification.success({
          message: intl.formatMessage({
            id: 'bankAccounts.update.notification.success',
          }),
          description: intl.formatMessage({
            id: 'bankAccounts.update.notification.successDescription',
          }),
        });

        queryChanel({
          invalidateQueries: [['bankAccount', data.id]],
          resetQueries: [[BANK_ACCOUNT_FILTER_KEY]],
        });
      },
    },
  );

  useServiceError(mutation.error);

  return mutation;
}

export function useUpdateStatusBankAccount() {
  const intl = useIntl();
  const queryClient = useQueryClient();

  const mutation = useMutation((id: number) => updateStatusBankAccount(id), {
    onSuccess(_, id) {
      message.success(
        intl.formatMessage({ id: 'bankAccounts.status.message.success' }),
      );

      queryClient.invalidateQueries(['bankAccount', id]);
    },
  });

  useServiceError(mutation.error);

  return mutation;
}

export function useDeleteBankAccount() {
  const intl = useIntl();
  const queryChanel = useQueryChanel();

  const mutation = useMutation((id: number) => deleteBankAccount(id), {
    onSuccess() {
      notification.success({
        message: intl.formatMessage({
          id: 'bankAccounts.remove.notification.success',
        }),
        description: intl.formatMessage({
          id: 'bankAccounts.remove.notification.successDescription',
        }),
      });

      queryChanel({
        invalidateQueries: [['bankAccounts']],
        resetQueries: [[BANK_ACCOUNT_FILTER_KEY]],
      });
    },
    onError(error) {
      notificationError(error, {
        translate: (errors) =>
          errors.map((err) =>
            err.includes('Bank Account cannot be deleted due to relation.')
              ? 'Não é possivel excluir conta bancária, pois ela tem vínculos com outros módulos.'
              : err,
          ),
      });
    },
  });

  return mutation;
}
